import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, TrendingUp, BarChart2, Shield, Zap, Brain, FileText } from 'lucide-react';

const TradingInvestment = () => {
  const useCases = [
    {
      icon: TrendingUp,
      title: 'Algorithmic Trading & Signal Generation',
      description: 'ML models that identify alpha signals across equities, FX, commodities, and derivatives — backtested and production-deployed with risk guardrails.',
      outcomes: ['Quantitative signal discovery', 'Automated strategy backtesting', 'Low-latency execution integration'],
    },
    {
      icon: Brain,
      title: 'Portfolio Analytics & Risk Intelligence',
      description: 'Real-time portfolio risk dashboards, factor exposure analysis, VaR modelling, and stress-testing frameworks built for investment teams.',
      outcomes: ['Real-time risk attribution', 'Tail-risk stress scenarios', 'Factor exposure decomposition'],
    },
    {
      icon: BarChart2,
      title: 'Market Surveillance & Anomaly Detection',
      description: 'AI systems that monitor trading activity for spoofing, layering, wash trades, and other market manipulation patterns — flagging in real time.',
      outcomes: ['Real-time manipulation detection', 'Regulatory alert generation', 'Pattern library across asset classes'],
    },
    {
      icon: FileText,
      title: 'Investment Research Automation',
      description: 'LLM-powered research assistants that ingest earnings calls, analyst reports, regulatory filings, and news — synthesising insights on demand.',
      outcomes: ['10x faster research synthesis', 'Sentiment and trend extraction', 'Automated earnings call analysis'],
    },
    {
      icon: Shield,
      title: 'Regulatory Compliance & Reporting AI',
      description: 'Automated MiFID II, EMIR, and CFTC reporting pipelines — reconciling trade data, generating submissions, and flagging exceptions.',
      outcomes: ['Automated regulatory submissions', 'Trade reconciliation at scale', 'Exception-based human review only'],
    },
    {
      icon: Zap,
      title: 'Trade Lifecycle & Operations Automation',
      description: 'Intelligent automation across post-trade processes — confirmations, settlement, reconciliation, and corporate actions processing.',
      outcomes: ['Straight-through processing rates 90%+', 'Reduced fails and breaks', 'Corporate actions automation'],
    },
  ];

  const caseStudies = [
    {
      title: 'Trading Operations Automation',
      result: 'Automated end-to-end post-trade workflow, reducing manual intervention by 80% and settlement fails by 60%.',
      link: '/case-studies/trading-automation',
    },
    {
      title: 'Payment & Settlement Automation',
      result: 'Streamlined payment processing for 1,000+ delivery partners with AI-driven exception handling.',
      link: '/case-studies/payment-automation',
    },
  ];

  const techStack = [
    'Python / pandas / QuantLib', 'XGBoost / LightGBM / PyTorch', 'Azure ML & Databricks',
    'Bloomberg & Refinitiv data feeds', 'FIX protocol integration', 'Apache Kafka for real-time streams',
    'GPT-4 for research synthesis', 'Power BI risk dashboards',
  ];

  return (
    <>
      <Helmet>
        <title>AI for Trading & Investment | Algorithmic Trading, Risk Analytics & Compliance AI | Agility</title>
        <meta
          name="description"
          content="AI solutions for trading firms and investment managers. Algorithmic trading signals, portfolio risk analytics, market surveillance, research automation, and regulatory compliance AI. Production systems in 3–8 weeks."
        />
        <meta
          name="keywords"
          content="trading AI, investment AI, algorithmic trading AI, portfolio risk analytics, market surveillance AI, investment research automation, trade compliance AI"
        />
        <link rel="canonical" href="https://agility.ai/industries/trading-investment" />
        <meta property="og:title" content="AI for Trading & Investment | Agility" />
        <meta property="og:description" content="AI solutions for trading firms and investment managers — from alpha signals to regulatory compliance." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-black pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-400 text-sm font-medium">Industry Solution</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI for <span className="text-teal-400">Trading & Investment</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              We build AI systems that generate alpha, manage risk, automate operations, and keep investment firms ahead of regulatory requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-md hover:bg-teal-400 transition-colors font-semibold text-lg"
              >
                Talk to a Trading AI Specialist <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center border border-gray-600 text-white px-8 py-4 rounded-md hover:border-teal-400 hover:text-teal-400 transition-colors font-semibold text-lg"
              >
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">AI Use Cases for Trading & Investment</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From signal generation to settlement — production AI across the full investment lifecycle.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-teal-500/40 transition-colors">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="text-teal-400 mt-0.5 flex-shrink-0" size={14} />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Trading & Investment Case Studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((cs, index) => (
              <a
                key={index}
                href={cs.link}
                className="block bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-500/40 transition-colors group"
              >
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-teal-400 transition-colors">{cs.title}</h3>
                <p className="text-gray-400 mb-4 text-sm">{cs.result}</p>
                <span className="text-teal-400 text-sm font-medium inline-flex items-center gap-1">
                  Read case study <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {techStack.map((tech, index) => (
                <span key={index} className="bg-gray-800 text-gray-300 px-4 py-2 rounded-full text-sm border border-gray-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-teal-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to bring AI to your trading and investment operations?</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Production-ready AI in 3–8 weeks. Let's identify your highest-impact use case first.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-semibold text-lg"
          >
            Book a Free Discovery Call <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );
};

export default TradingInvestment;
